import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';
import { toast } from 'sonner';
import {
  FlaskConical, LayoutDashboard, FileText, Settings, LogOut, Menu, X, ChevronLeft, CheckCircle2, FileSignature, Database, Upload,
  Sparkles, PanelLeftOpen, PanelLeftClose, ChevronDown, Bell, User
} from 'lucide-react';
import { getTemplateForTest } from '../constants/LabTemplates';
import './LabDashboard.css';

const ClinicMedicalIcon = ({ size = 20, className = "" }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 576 512" 
    width={size} 
    height={size} 
    className={className}
  >
    {/* White house base & chimney & roof */}
    <path 
      fill="white" 
      d="M575.8 255.5c0 18-15 32.1-32 32.1h-32l.7 160.2c.2 35.5-28.5 64.3-64 64.3H128.1c-35.3 0-64-28.7-64-64V287.6H32c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L416 100.7V64c0-17.7 14.3-32 32-32h32c17.7 0 32 14.3 32 32V185l52.8 46.4c8 7 12 15 11 24z"
    />
    {/* Blue cross cutout inside the house base */}
    <path 
      fill="#2563eb" 
      d="M272 192c-8.8 0-16 7.2-16 16v48H208c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h48v48c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V320h48c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H320V208c0-8.8-7.2-16-16-16H272z"
    />
  </svg>
);



function LabDashboard() {
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('active');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('klinik_lab_sidebar_collapsed');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('klinik_lab_sidebar_collapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [labInfo, setLabInfo] = useState(null);
  const [activeTests, setActiveTests] = useState([]);
  const [completedTests, setCompletedTests] = useState([]);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [testResults, setTestResults] = useState('');
  const [structuredData, setStructuredData] = useState({});
  const [entryMode, setEntryMode] = useState('structured'); // 'structured', 'text', 'upload'
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditingCompleted, setIsEditingCompleted] = useState(false);

  useEffect(() => {
    if (currentUser) fetchLabInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (!labInfo) return;

    fetchPrescriptions();

    // 1. Supabase Realtime Subscription (Fires if replication is enabled natively)
    const subscription = supabase
      .channel('lab_prescriptions')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'prescriptions' },
        (payload) => {
          fetchPrescriptions();
        }
      )
      .subscribe();

    // 2. Tab Focus trigger - lightweight safety net
    window.addEventListener('focus', fetchPrescriptions);

    return () => {
      supabase.removeChannel(subscription);
      window.removeEventListener('focus', fetchPrescriptions);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labInfo]);

  async function fetchLabInfo() {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', currentUser.id)
        .single();
      if (error) throw error;
      setLabInfo(data);
    } catch (error) {
      toast.error('Failed to load lab profile');
    }
  }

  async function fetchPrescriptions() {
    try {
      const { data, error } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('clinic_id', labInfo.clinic_id)
        .eq('sent_to_lab', true)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const prescriptions = data || [];
      setActiveTests(prescriptions.filter(p => ['pending', 'sample_collected', 'in_progress_lab'].includes(p.lab_status)));
      setCompletedTests(prescriptions.filter(p => ['report_uploaded', 'completed'].includes(p.lab_status)));
    } catch (error) {
      toast.error('Failed to load lab tests');
    }
  }

  function handleViewPrescription(prescription) {
    setSelectedPrescription(prescription);
    setUploadFile(null);
    setIsEditingCompleted(false);

    if (!prescription.lab_results) {
      // New blank prescription
      const initialData = {};
      let hasTemplate = false;
      (prescription.tests || []).forEach(testGroup => {
        const template = getTemplateForTest(testGroup);
        if (template) {
          hasTemplate = true;
          initialData[testGroup] = {};
          template.forEach(p => initialData[testGroup][p.key] = '');
        }
      });
      setStructuredData(initialData);
      setTestResults('');
      setEntryMode(hasTemplate ? 'structured' : 'text');
    } else {
      // Existing prescription
      try {
        const parsed = JSON.parse(prescription.lab_results);
        if (parsed.structured) {
          setStructuredData(parsed.data || {});
          setEntryMode('structured');
        } else if (parsed.mode === 'text') {
          setTestResults(parsed.text || '');
          setEntryMode('text');
        } else if (parsed.mode === 'upload') {
          setEntryMode('upload');
        } else {
          setTestResults(prescription.lab_results);
          setEntryMode('text');
        }
      } catch (e) {
        setTestResults(prescription.lab_results);
        setEntryMode('text');
      }
    }
  }

  async function handleMarkAsCompleted() {
    if (!selectedPrescription) return;

    if (entryMode === 'text' && !testResults.trim()) {
      toast.error('Please enter the test results or a summary before completing');
      return;
    }

    if (entryMode === 'upload' && !uploadFile && !isEditingCompleted) {
      toast.error('Please select a file to upload');
      return;
    }

    if (entryMode === 'structured') {
      let hasData = false;
      for (const group in structuredData) {
        if (structuredData[group]) {
          for (const key in structuredData[group]) {
            if (structuredData[group][key] !== '' && structuredData[group][key] !== null && structuredData[group][key] !== undefined) {
              hasData = true;
              break;
            }
          }
        }
        if (hasData) break;
      }
      if (!hasData) {
        toast.error('Please enter at least one test result before completing');
        return;
      }
    }

    setLoading(true);
    let finalResults = testResults;

    let wasEditedBefore = false;
    let oldEditedAt = null;
    let oldUploadContent = null;
    try {
      const p = JSON.parse(selectedPrescription.lab_results);
      if (p.is_edited) {
        wasEditedBefore = true;
        oldEditedAt = p.edited_at;
      }
      if (p.mode === 'upload') oldUploadContent = p;
    } catch(e) {}

    const isNowEdited = isEditingCompleted || wasEditedBefore;
    const basePayload = {
      is_edited: isNowEdited,
      edited_at: isEditingCompleted ? new Date().toISOString() : oldEditedAt
    };

    if (entryMode === 'structured') {
      finalResults = JSON.stringify({ structured: true, data: structuredData, ...basePayload });
    } else if (entryMode === 'text') {
      finalResults = JSON.stringify({ structured: false, mode: 'text', text: testResults, ...basePayload });
    }

    try {
      if (entryMode === 'upload') {
        if (uploadFile) {
          const fileExt = uploadFile.name.split('.').pop();
          const fileName = `${selectedPrescription.id}-${Date.now()}.${fileExt}`;
          const { error: uploadError } = await supabase.storage
            .from('lab_reports')
            .upload(fileName, uploadFile);

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('lab_reports')
            .getPublicUrl(fileName);

          finalResults = JSON.stringify({
            structured: false,
            mode: 'upload',
            fileName: uploadFile.name,
            url: publicUrl,
            ...basePayload
          });
        } else if (isEditingCompleted && oldUploadContent) {
          finalResults = JSON.stringify({
            ...oldUploadContent,
            ...basePayload
          });
        }
      }

      const { error } = await supabase
        .from('prescriptions')
        .update({
          status: 'awaiting_review',
          lab_status: 'report_uploaded',
          lab_results: finalResults,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedPrescription.id);

      if (error) throw error;

      toast.success('Lab tests marked as completed and results saved');
      setSelectedPrescription(null);
      setTestResults('');
      setUploadFile(null);
      fetchPrescriptions();
    } catch (error) {
      toast.error(`Error updating lab test: ${error.message}`);
    }
    setLoading(false);
  }

  async function updateLabStatus(prescriptionId, newStatus) {
    if (!prescriptionId) return;
    try {
      const { error } = await supabase
        .from('prescriptions')
        .update({ lab_status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', prescriptionId);

      if (error) throw error;
      toast.success('Status updated successfully');

      // Update local state temporarily
      if (selectedPrescription?.id === prescriptionId) {
        setSelectedPrescription({ ...selectedPrescription, lab_status: newStatus });
      }
      fetchPrescriptions();
    } catch (error) {
      toast.error('Failed to update status: ' + error.message);
    }
  }

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  if (!labInfo) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--gray-50)' }}>
        Loading Lab Dashboard...
      </div>
    );
  }

  const filteredActiveTests = activeTests.filter(p =>
    p.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.patient_phone?.includes(searchTerm)
  );

  const filteredCompletedTests = completedTests.filter(p =>
    p.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.patient_phone?.includes(searchTerm)
  );

  const calculateTAT = (createdAt) => {
    const createdTime = new Date(createdAt).getTime();
    const diffHours = (Date.now() - createdTime) / (1000 * 60 * 60);

    if (diffHours < 2) return { text: '< 2h', colorClass: 'tat-green' };
    if (diffHours < 4) return { text: '2-4h', colorClass: 'tat-yellow' };
    return { text: '> 4h', colorClass: 'tat-red' };
  };

  const displayEditedBadge = (labResultsString) => {
    if (!labResultsString) return null;
    try {
      const parsed = JSON.parse(labResultsString);
      if (parsed.is_edited) {
        return (
          <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: 'bold', color: '#ea580c', background: '#ffedd5', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>
            (Edited)
          </span>
        );
      }
    } catch(e) {}
    return null;
  };

  const isCompletedStatus = ['report_uploaded', 'completed'].includes(selectedPrescription?.lab_status);
  const canEdit = isCompletedStatus && selectedPrescription?.updated_at && 
    (Date.now() - new Date(selectedPrescription.updated_at).getTime() < 24 * 60 * 60 * 1000);

  return (
    <>
      <div className={`lab-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {mobileMenuOpen && (
          <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
        )}

        <aside className={`lab-sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-logo-section">
            <div className="logo-badge" style={{ backgroundColor: '#2563eb', borderRadius: '50%', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'none' }}>
              <ClinicMedicalIcon size={20} />
            </div>
            {!isSidebarCollapsed && <span className="sidebar-logo-text" style={{ color: '#2563eb', fontWeight: 800 }}>Klinik</span>}
            {!isSidebarCollapsed && (
              <button className="sidebar-toggle-btn" onClick={() => setIsSidebarCollapsed(true)}>
                <PanelLeftClose size={18} />
              </button>
            )}
          </div>

          <nav className="sidebar-nav">
            <button
              className={`nav-item ${activeTab === 'active' ? 'active' : ''}`}
              onClick={() => { setActiveTab('active'); setMobileMenuOpen(false); setSelectedPrescription(null); }}
              title={isSidebarCollapsed ? "Pending Tests" : ""}
            >
              <FileSignature size={20} />
              {!isSidebarCollapsed && <span>Pending Tests</span>}
              {activeTests.length > 0 && !isSidebarCollapsed && (
                <span className="badge-count">{activeTests.length}</span>
              )}
            </button>
            <button
              className={`nav-item ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => { setActiveTab('completed'); setMobileMenuOpen(false); setSelectedPrescription(null); }}
              title={isSidebarCollapsed ? "Completed Tests" : ""}
            >
              <CheckCircle2 size={20} />
              {!isSidebarCollapsed && <span>Completed Tests</span>}
            </button>
          </nav>

          <div className="sidebar-footer">
            <button className="logout-btn-sidebar" onClick={handleLogout} title={isSidebarCollapsed ? "Logout" : ""}>
              <LogOut size={18} />
              {!isSidebarCollapsed && <span>Logout</span>}
            </button>
          </div>
        </aside>

        <main className="lab-main">
          <header className="lab-topbar">
            <div className="topbar-left">
              <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <Menu size={22} />
              </button>
              {isSidebarCollapsed && (
                <button className="desktop-expand-btn" onClick={() => setIsSidebarCollapsed(false)}>
                  <PanelLeftOpen size={20} />
                </button>
              )}
              <h1 className="topbar-title">
                {activeTab === 'active' && 'Pending Lab Tests'}
                {activeTab === 'completed' && 'Completed Lab Tests'}
              </h1>
            </div>

            <div className="topbar-right">
              <button className="notification-btn" title="Notifications">
                <Bell size={20} />
                <span className="notification-badge" />
              </button>

              <div className="profile-dropdown-container">
                <button className="profile-dropdown-trigger" onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}>
                  <div className="avatar-circle">
                    {labInfo?.name?.charAt(0).toUpperCase() || 'L'}
                  </div>
                  <span className="profile-name">{labInfo?.name || 'Lab Portal'}</span>
                  <ChevronDown size={16} className={`chevron-icon ${profileDropdownOpen ? 'open' : ''}`} />
                </button>

                {profileDropdownOpen && (
                  <>
                    <div className="dropdown-backdrop" onClick={() => setProfileDropdownOpen(false)} />
                    <div className="dropdown-menu">
                      <div className="dropdown-header">
                        <h4>{labInfo?.name}</h4>
                        <p>Laboratory Diagnostic Portal</p>
                      </div>
                      <button className="dropdown-item logout" onClick={() => { handleLogout(); setProfileDropdownOpen(false); }}>
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          <div className="lab-content dashboard-split-view">
            <div className="master-list-pane">
              <div className="search-bar-container">
                <input
                  type="text"
                  placeholder="Search by name or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              {activeTab === 'active' && (
                <div className="prescriptions-list">
                  {filteredActiveTests.length === 0 ? (
                    <div className="empty-state mini">
                      <p>No tests found.</p>
                    </div>
                  ) : (
                    filteredActiveTests.map(prescription => (
                      <div
                        key={prescription.id}
                        className={`prescription-card ${selectedPrescription?.id === prescription.id ? 'active-selection' : ''}`}
                        onClick={() => handleViewPrescription(prescription)}
                      >
                        <div className="card-header">
                          <h4>{prescription.patient_name}</h4>
                          <span className={`tat-badge ${calculateTAT(prescription.created_at).colorClass}`} title={`Created: ${new Date(prescription.created_at).toLocaleString()}`}>
                            {calculateTAT(prescription.created_at).text}
                          </span>
                        </div>
                        <div className="card-content" style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <p style={{ margin: 0 }}>Tests: {(prescription.tests || []).length} items</p>
                          <select
                            value={prescription.lab_status || 'pending'}
                            onChange={(e) => updateLabStatus(prescription.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className={`status-select-mini ${prescription.lab_status || 'pending'}`}
                          >
                            <option value="pending">Awaiting Sample</option>
                            <option value="sample_collected">Sample Collected</option>
                            <option value="in_progress_lab">Testing in Progress</option>
                          </select>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'completed' && (
                <div className="prescriptions-list">
                  {filteredCompletedTests.length === 0 ? (
                    <div className="empty-state mini">
                      <p>No tests found.</p>
                    </div>
                  ) : (
                    filteredCompletedTests.map(prescription => (
                      <div
                        key={prescription.id}
                        className={`prescription-card completed ${selectedPrescription?.id === prescription.id ? 'active-selection' : ''}`}
                        onClick={() => handleViewPrescription(prescription)}
                      >
                        <div className="card-header">
                          <h4>{prescription.patient_name}</h4>
                          <span className="badge-success">Completed</span>
                        </div>
                        <div className="card-content">
                          <p>Completed: {new Date(prescription.lab_completed_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="detail-view-pane">
              {!selectedPrescription ? (
                <div className="empty-detail-state">
                  <FlaskConical size={64} style={{ color: '#d1d5db', margin: '0 auto 1rem' }} />
                  <h3>Select a patient to view details</h3>
                  <p>Click on any card from the list to start processing</p>
                </div>
              ) : (
                <div className="prescription-detail-view">
                  <div className="detail-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <button className="btn-back mobile-only" onClick={() => setSelectedPrescription(null)} style={{ background: 'none', border: 'none', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '1rem' }}>
                        <ChevronLeft size={18} />
                        Back
                      </button>
                      <h2 style={{ margin: 0, color: '#111827' }}>Test Requirements</h2>
                    </div>
                    {['pending', 'sample_collected', 'in_progress_lab'].includes(selectedPrescription.lab_status) && (
                      <select
                        value={selectedPrescription.lab_status}
                        onChange={(e) => updateLabStatus(selectedPrescription.id, e.target.value)}
                        className="status-select-header"
                      >
                        <option value="pending">Awaiting Sample</option>
                        <option value="sample_collected">Sample Collected</option>
                        <option value="in_progress_lab">Testing In Progress</option>
                      </select>
                    )}
                  </div>

                  <div className="patient-info-card" style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '0.75rem', marginBottom: '2rem' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#374151', fontSize: '1.1rem' }}>Patient Information</h3>
                    <div className="info-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                      <div className="info-item"><span className="label" style={{ color: '#6b7280', fontSize: '0.95rem', marginRight: '0.5rem' }}>Name:</span><span className="value" style={{ color: '#111827', fontWeight: '600' }}>{selectedPrescription.patient_name}</span></div>
                      <div className="info-item"><span className="label" style={{ color: '#6b7280', fontSize: '0.95rem', marginRight: '0.5rem' }}>Age:</span><span className="value" style={{ color: '#111827', fontWeight: '600' }}>{selectedPrescription.patient_age} years</span></div>
                      <div className="info-item"><span className="label" style={{ color: '#6b7280', fontSize: '0.95rem', marginRight: '0.5rem' }}>Gender:</span><span className="value" style={{ color: '#111827', fontWeight: '600' }}>{selectedPrescription.patient_gender}</span></div>
                      <div className="info-item"><span className="label" style={{ color: '#6b7280', fontSize: '0.95rem', marginRight: '0.5rem' }}>Doctor:</span><span className="value" style={{ color: '#111827', fontWeight: '600' }}>Dr. {selectedPrescription.doctor_name}</span></div>
                    </div>
                  </div>

                  <div className="medicines-card">
                    <h3 style={{ marginBottom: '0.5rem', color: '#111827', fontSize: '1.1rem' }}>Tests Ordered</h3>
                    <div className="tests-ordered-grid" style={{ marginBottom: '2rem', color: '#374151', fontSize: '1rem', lineHeight: '1.6' }}>
                      {(selectedPrescription.tests || []).join(', ')}
                    </div>

                    <div className="test-results-section" style={{ marginTop: '2rem' }}>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h3 style={{ margin: 0, color: '#111827' }}>
                          Test Results / Data Entry
                          {isCompletedStatus && displayEditedBadge(selectedPrescription.lab_results)}
                        </h3>

                        {(['pending', 'sample_collected', 'in_progress_lab'].includes(selectedPrescription.lab_status) || isEditingCompleted) && (
                          <div className="entry-mode-toggles" style={{ display: 'flex', gap: '0.5rem', background: '#f3f4f6', padding: '0.25rem', borderRadius: '0.5rem' }}>
                            <button
                              onClick={() => setEntryMode('structured')}
                              style={{ padding: '0.5rem 0.75rem', border: 'none', background: entryMode === 'structured' ? 'white' : 'transparent', borderRadius: '0.25rem', boxShadow: entryMode === 'structured' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem', fontWeight: entryMode === 'structured' ? '600' : '500', color: entryMode === 'structured' ? '#0ea5e9' : '#6b7280' }}
                            >
                              <Database size={16} /> Structured
                            </button>
                            <button
                              onClick={() => setEntryMode('text')}
                              style={{ padding: '0.5rem 0.75rem', border: 'none', background: entryMode === 'text' ? 'white' : 'transparent', borderRadius: '0.25rem', boxShadow: entryMode === 'text' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem', fontWeight: entryMode === 'text' ? '600' : '500', color: entryMode === 'text' ? '#0ea5e9' : '#6b7280' }}
                            >
                              <FileText size={16} /> Free Text
                            </button>
                            <button
                              onClick={() => setEntryMode('upload')}
                              style={{ padding: '0.5rem 0.75rem', border: 'none', background: entryMode === 'upload' ? 'white' : 'transparent', borderRadius: '0.25rem', boxShadow: entryMode === 'upload' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem', fontWeight: entryMode === 'upload' ? '600' : '500', color: entryMode === 'upload' ? '#0ea5e9' : '#6b7280' }}
                            >
                              <Upload size={16} /> Upload PDF
                            </button>
                          </div>
                        )}
                      </div>

                      {['pending', 'sample_collected', 'in_progress_lab'].includes(selectedPrescription.lab_status) || isEditingCompleted ? (
                        <>
                          {entryMode === 'text' && (
                            <textarea
                              value={testResults}
                              onChange={(e) => setTestResults(e.target.value)}
                              placeholder="Enter free-text remarks, test observations, or summaries here..."
                              style={{ width: '100%', minHeight: '150px', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', fontFamily: 'inherit', resize: 'vertical' }}
                            />
                          )}

                          {entryMode === 'structured' && (
                            <div className="structured-entry-container" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                              {(() => {
                                const rawResults = selectedPrescription.lab_results || '';
                                const sortedTests = [...(selectedPrescription.tests || [])].sort((a, b) => {
                                  const aDone = rawResults.includes(`"${a}"`);
                                  const bDone = rawResults.includes(`"${b}"`);
                                  if (aDone && !bDone) return 1;
                                  if (!aDone && bDone) return -1;
                                  return 0;
                                });

                                return sortedTests.map(testGroup => {
                                  const template = getTemplateForTest(testGroup);
                                  const isAlreadyDone = rawResults.includes(`"${testGroup}"`);

                                  if (!template) {
                                    return (
                                      <div key={testGroup} style={{ padding: '1rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.5rem', opacity: isAlreadyDone ? 0.6 : 1 }}>
                                        <p style={{ margin: 0, color: '#b45309', fontSize: '0.9rem' }}>No structured template available for <strong>{testGroup}</strong>. Use Free Text or Upload mode.</p>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div key={testGroup} className="test-group-block" style={{ opacity: isAlreadyDone ? 0.65 : 1, transition: 'opacity 0.2s' }}>
                                      <h4 style={{ margin: '0 0 1rem 0', color: '#111827', borderBottom: '2px solid #e5e7eb', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span>{testGroup} Form</span>
                                        {isAlreadyDone && <span style={{ fontSize: '0.75rem', background: '#ecfdf5', color: '#065f46', padding: '0.2rem 0.5rem', borderRadius: '999px', border: '1px solid #a7f3d0' }}>Already Completed</span>}
                                      </h4>
                                    <div className="structured-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }}>
                                      {template.map(param => {
                                        const val = structuredData[testGroup]?.[param.key] || '';
                                        const isTextType = param.min === 0 && param.max === 0;
                                        
                                        const numVal = parseFloat(val);
                                        const isLow = !isTextType && !isNaN(numVal) && numVal < param.min;
                                        const isHigh = !isTextType && !isNaN(numVal) && numVal > param.max;
                                        const isFlagged = isLow || isHigh;

                                        return (
                                          <div key={param.key} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr', alignItems: 'center', gap: '1rem', background: '#f9fafb', padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                                            <span style={{ fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>{param.name}</span>

                                            <div style={{ position: 'relative' }}>
                                              {param.unit === 'Text' ? (
                                                <textarea
                                                  value={val}
                                                  onChange={(e) => {
                                                    setStructuredData(prev => ({
                                                      ...prev,
                                                      [testGroup]: {
                                                        ...prev[testGroup],
                                                        [param.key]: e.target.value
                                                      }
                                                    }));
                                                  }}
                                                  style={{
                                                    width: '100%', padding: '0.5rem', border: '1px solid #d1d5db',
                                                    borderRadius: '0.375rem', color: '#111827', outline: 'none',
                                                    minHeight: '60px', resize: 'vertical', fontFamily: 'inherit'
                                                  }}
                                                  placeholder="Enter findings / observations..."
                                                />
                                              ) : (
                                                <input
                                                  type={isTextType ? "text" : "number"}
                                                  step={isTextType ? undefined : "any"}
                                                  value={val}
                                                  onChange={(e) => {
                                                    setStructuredData(prev => ({
                                                      ...prev,
                                                      [testGroup]: {
                                                        ...prev[testGroup],
                                                        [param.key]: e.target.value
                                                      }
                                                    }));
                                                  }}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                                      handleMarkAsCompleted();
                                                    }
                                                  }}
                                                  style={{
                                                    width: '100%', padding: '0.5rem', paddingRight: isTextType ? '0.5rem' : '2.5rem', border: isFlagged ? '2px solid #ef4444' : '1px solid #d1d5db',
                                                    borderRadius: '0.375rem', fontWeight: isFlagged ? '700' : 'normal', color: isFlagged ? '#dc2626' : '#111827',
                                                    outline: 'none', transition: 'all 0.2s'
                                                  }}
                                                  placeholder={isTextType ? "Enter result..." : `Exp: ${param.min} - ${param.max}`}
                                                />
                                              )}

                                              {isFlagged && !isTextType && (
                                                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#ef4444', fontWeight: 'bold', fontSize: '0.8rem' }}>
                                                  {isLow ? '(L)' : '(H)'}
                                                </span>
                                              )}
                                            </div>

                                            <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                              {param.unit} {param.unit !== 'Text' && !isTextType && <><br /> <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>({param.min} - {param.max})</span></>}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              });
                              })()}
                            </div>
                          )}

                          {entryMode === 'upload' && (
                            <div className="upload-container" style={{ textAlign: 'center', padding: '3rem', border: '2px dashed #cbd5e1', borderRadius: '0.5rem', background: '#f8fafc' }}>
                              <Upload size={48} style={{ color: '#94a3b8', margin: '0 auto 1rem' }} />
                              <h4>Upload Lab Report</h4>
                              <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                                {uploadFile ? `Selected: ${uploadFile.name}` : 'Drag and drop PDF or Image files here, or click to select files.'}
                              </p>
                              <input
                                type="file"
                                id="report-upload"
                                style={{ display: 'none' }}
                                accept=".pdf,image/*"
                                onChange={(e) => setUploadFile(e.target.files[0])}
                              />
                              <label htmlFor="report-upload" style={{ background: 'white', border: '1px solid #cbd5e1', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '500', color: '#334155', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                                Browse Files
                              </label>
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ padding: '1.5rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0', color: '#334155' }}>
                          {selectedPrescription.lab_results && selectedPrescription.lab_results.startsWith('{"structured":true') ? (
                            <div className="structured-display">
                              {(() => {
                                try {
                                  const parsed = JSON.parse(selectedPrescription.lab_results).data;
                                  return Object.keys(parsed).map(testGroup => (
                                    <div key={testGroup} style={{ marginBottom: '1.5rem' }}>
                                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#111827', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.25rem' }}>{testGroup}</h4>
                                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.5rem', fontSize: '0.9rem' }}>
                                        <div style={{ fontWeight: '600', color: '#64748b' }}>Parameter</div>
                                        <div style={{ fontWeight: '600', color: '#64748b' }}>Result</div>
                                        <div style={{ fontWeight: '600', color: '#64748b' }}>Ref Range</div>
                                        {Object.keys(parsed[testGroup]).map(key => {
                                          const template = getTemplateForTest(testGroup);
                                          const paramDef = template?.find(p => p.key === key);
                                          const val = parsed[testGroup][key];
                                          const numVal = parseFloat(val);
                                          const isLow = paramDef && !isNaN(numVal) && numVal < paramDef.min;
                                          const isHigh = paramDef && !isNaN(numVal) && numVal > paramDef.max;
                                          const isFlagged = isLow || isHigh;
                                          return (
                                            <React.Fragment key={key}>
                                              <div>{paramDef ? paramDef.name : key}</div>
                                              <div style={{ fontWeight: '600', color: isFlagged ? '#dc2626' : '#111827' }}>
                                                {val} {isFlagged && (isLow ? '(L)' : '(H)')} {paramDef?.unit}
                                              </div>
                                              <div style={{ color: '#64748b' }}>{paramDef ? `${paramDef.min} - ${paramDef.max}` : '-'}</div>
                                            </React.Fragment>
                                          )
                                        })}
                                      </div>
                                    </div>
                                  ));
                                } catch (e) { return <p>Error parsing results</p>; }
                              })()}
                            </div>
                          ) : (
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                              {(() => {
                                try {
                                  const parsed = JSON.parse(selectedPrescription.lab_results);
                                  if (parsed.mode === 'upload') {
                                    return (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem' }}>
                                        <FileText size={32} color="#0ea5e9" />
                                        <div style={{ flex: 1 }}>
                                          <h4 style={{ margin: '0 0 0.25rem 0' }}>Attached Report</h4>
                                          <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>{parsed.fileName}</p>
                                        </div>
                                        <a href={parsed.url} target="_blank" rel="noopener noreferrer" style={{ padding: '0.5rem 1rem', background: '#0ea5e9', color: 'white', textDecoration: 'none', borderRadius: '0.375rem', fontWeight: '500', fontSize: '0.9rem' }}>
                                          View File
                                        </a>
                                      </div>
                                    );
                                  }
                                } catch (e) { }
                                return selectedPrescription.lab_results;
                              })()}
                            </div>
                          )}

                          {canEdit && !isEditingCompleted && (
                            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
                              <button
                                onClick={() => setIsEditingCompleted(true)}
                                style={{ background: 'white', color: '#0ea5e9', border: '1px solid #e0f2fe', padding: '0.5rem 1rem', borderRadius: '0.375rem', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                              >
                                Edit Results
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {(['pending', 'sample_collected', 'in_progress_lab'].includes(selectedPrescription.lab_status) || isEditingCompleted) && (
                    <div className="actions-section" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                      {isEditingCompleted && (
                        <button onClick={() => setIsEditingCompleted(false)} style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                      )}
                      <button className="btn-complete" onClick={handleMarkAsCompleted} disabled={loading} style={{ background: '#0ea5e9', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s' }}>
                        {loading ? 'Processing...' : (isEditingCompleted ? 'Save Changes' : 'Mark Complete')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default LabDashboard;